<template>
  <div class="stlFullscreen" @click.self="$emit('close')">
    <div class="stlFullscreenToolbar" @click.self="$emit('close')">
      <div class="stlFullscreenTitle">{{ lookTitle }}</div>
      <button
        type="button"
        class="stlFullscreenClose"
        aria-label="Ansicht schliessen"
        @click="$emit('close')"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </div>

    <div class="stlFullscreenViewport" @click.self="$emit('close')">
      <button
        type="button"
        class="stlFullscreenNav stlFullscreenNavPrev"
        aria-label="Vorherigen Look anzeigen"
        :disabled="isPrevDisabled"
        @click="$emit('request-previous')"
      >
        <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>

      <div class="stlFullscreenLook">
        <shop-the-look-item
          :key="activeLook.id"
          :config="activeLook"
          :show-magnifier="false"
          :show-master-link="false"
        />
      </div>

      <div class="stlFullscreenMeta" @click.self="$emit('close')">

        <div v-if="isVariantGroup" class="stlFullscreenVariants" @click.stop>
          <button
            type="button"
            class="stlFullscreenVariantStep"
            aria-label="Vorherige Variante anzeigen"
            @click="showPreviousVariant"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>

          <div class="stlFullscreenVariantList">
            <button
              v-for="variant in variants"
              :key="variant.id"
              type="button"
              class="stlFullscreenVariantThumb"
              :class="{ active: Number(variant.id) === Number(activeLook.id) }"
              :aria-label="variantTitle(variant)"
              @click="selectVariant(variant.id)"
            >
              <img :src="variant.imageUrl" :alt="variantTitle(variant)" loading="lazy">
            </button>
          </div>

          <button
            type="button"
            class="stlFullscreenVariantStep"
            aria-label="Naechste Variante anzeigen"
            @click="showNextVariant"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>

        <div v-if="lookTags.length" class="stlFullscreenTags">
          <button
            v-for="tag in lookTags"
            :key="tag.id"
            type="button"
            class="stlFullscreenTag"
            @click="$emit('select-tag', tag.id)"
          >
            {{ tag.value }}
          </button>
        </div>
      </div>

      <div v-if="isLoading" class="stlFullscreenLoading" role="status" aria-live="polite">
        <span class="stlFullscreenLoadingSpinner" aria-hidden="true" />
        <span>Lade weitere Looks...</span>
      </div>

      <button
        type="button"
        class="stlFullscreenNav stlFullscreenNavNext"
        aria-label="Naechsten Look anzeigen"
        :disabled="isNextDisabled"
        @click="$emit('request-next')"
      >
        <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import ShopTheLookItem from './ShopTheLookItem.vue'

export default {
  name: 'ShopTheLookFullscreen',
  components: {
    ShopTheLookItem
  },
  props: {
    look: {
      type: Object,
      required: true
    },
    selectedLookId: {
      type: [Number, String],
      default: 0
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isPrevDisabled: {
      type: Boolean,
      default: false
    },
    isNextDisabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      activeVariantId: this.resolveInitialVariantId()
    }
  },
  computed: {
    variants () {
      return this.buildVariants(this.look)
    },
    activeLook () {
      return this.findVariantById(this.activeVariantId) || this.variants[0] || this.look
    },
    activeVariantIndex () {
      return this.variants.findIndex(variant => Number(variant.id) === Number(this.activeLook.id))
    },
    isVariantGroup () {
      return this.variants.length > 1
    },
    lookTitle () {
      const title = this.activeLook && this.activeLook.title ? String(this.activeLook.title) : ''

      if (!title) {
        return ''
      }

      if (typeof document === 'undefined') {
        return title.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
      }

      const parser = document.createElement('div')
      parser.innerHTML = title

      return (parser.textContent || parser.innerText || '').replace(/\s+/g, ' ').trim()
    },
    lookTags () {
      if (this.activeLook && Array.isArray(this.activeLook.tags) && this.activeLook.tags.length) {
        return this.activeLook.tags.filter(tag => Number(tag.id) > 0 && tag.value)
      }

      if (!this.look || !Array.isArray(this.look.tags)) {
        return []
      }

      return this.look.tags.filter(tag => Number(tag.id) > 0 && tag.value)
    }
  },
  watch: {
    look: {
      deep: true,
      handler () {
        this.activeVariantId = this.resolveInitialVariantId()
      }
    },
    selectedLookId () {
      this.activeVariantId = this.resolveInitialVariantId()
    }
  },
  mounted () {
    if (typeof document !== 'undefined') {
      document.body.classList.add('stlFullscreenOpen')
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.onKeydown)
    }
  },
  beforeDestroy () {
    if (typeof document !== 'undefined') {
      document.body.classList.remove('stlFullscreenOpen')
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.onKeydown)
    }
  },
  methods: {
    buildVariants (look) {
      const variants = []
      const seenIds = {}

      const addLook = (variant) => {
        const id = Number(variant && variant.id)
        if (!variant || id <= 0 || seenIds[id]) {
          return
        }

        seenIds[id] = true
        variants.push(variant)
      }

      addLook(look)

      if (look && Array.isArray(look.children)) {
        look.children.forEach(addLook)
      }

      return variants
    },
    resolveInitialVariantId () {
      const variants = this.buildVariants(this.look)
      const selectedLookId = Number(this.selectedLookId)

      if (selectedLookId > 0 && variants.some(variant => Number(variant.id) === selectedLookId)) {
        return selectedLookId
      }

      const backendSelectedLookId = Number(this.look && this.look.selectedLookId)
      if (backendSelectedLookId > 0 && variants.some(variant => Number(variant.id) === backendSelectedLookId)) {
        return backendSelectedLookId
      }

      const matchedLookIds = Array.isArray(this.look && this.look.matchedLookIds) ? this.look.matchedLookIds : []
      for (let i = 0; i < matchedLookIds.length; i++) {
        const matchedLookId = Number(matchedLookIds[i])
        if (matchedLookId > 0 && variants.some(variant => Number(variant.id) === matchedLookId)) {
          return matchedLookId
        }
      }

      return variants.length ? Number(variants[0].id) : 0
    },
    findVariantById (lookId) {
      const normalizedLookId = Number(lookId)
      return this.variants.find(variant => Number(variant.id) === normalizedLookId)
    },
    selectVariant (lookId) {
      const variant = this.findVariantById(lookId)
      if (!variant) {
        return
      }

      this.activeVariantId = Number(variant.id)
      this.$emit('select-variant', this.activeVariantId)
    },
    showNextVariant () {
      if (!this.variants.length) {
        return
      }

      const nextIndex = this.activeVariantIndex >= 0 ? (this.activeVariantIndex + 1) % this.variants.length : 0
      this.selectVariant(this.variants[nextIndex].id)
    },
    showPreviousVariant () {
      if (!this.variants.length) {
        return
      }

      const previousIndex = this.activeVariantIndex > 0 ? this.activeVariantIndex - 1 : this.variants.length - 1
      this.selectVariant(this.variants[previousIndex].id)
    },
    variantTitle (variant) {
      const title = variant && variant.title ? String(variant.title) : ''
      const cleanTitle = this.stripHtml(title)

      return cleanTitle || (variant && variant.ariaLabel) || `Look ${variant.id}`
    },
    stripHtml (value) {
      if (!value) {
        return ''
      }

      if (typeof document === 'undefined') {
        return String(value).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
      }

      const parser = document.createElement('div')
      parser.innerHTML = String(value)

      return (parser.textContent || parser.innerText || '').replace(/\s+/g, ' ').trim()
    },
    onKeydown (event) {
      if (!event) {
        return
      }

      if (event.key === 'Escape') {
        this.$emit('close')
        return
      }

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault()
        this.$emit('request-next')
        return
      }

      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault()
        this.$emit('request-previous')
      }
    }
  }
}
</script>
